import { createEntityAdapter, EntityAdapter } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import { TherapyLimitationsType } from '@mona/models';
import { EncountersActions } from '@mona/pdms/data-access-encounters';
import { TherapyLimitationsState } from '../../entities';
import { TherapyLimitationsActions } from '../actions';

export const therapyLimitationsAdapter: EntityAdapter<TherapyLimitationsType> =
    createEntityAdapter<TherapyLimitationsType>();

export const initialState: TherapyLimitationsState = therapyLimitationsAdapter.getInitialState({
    error: null,
    isLoading: false,
});

export const therapyLimitationsReducer = createReducer(
    initialState,
    on(TherapyLimitationsActions.loadTherapyLimitations, state => ({ ...state, isLoading: true })),
    on(TherapyLimitationsActions.loadTherapyLimitationsSuccess, (state, { data }) =>
        // Use upsertMany instead of setAll so optimistic local entries (added/updated between
        // dispatching `loadTherapyLimitations` and the response arriving, or by a WS push that
        // landed before the GET completed) are not wiped out. Deletions propagate via the
        // dedicated `addTherapyLimitationsChanges` action (WS push).
        therapyLimitationsAdapter.upsertMany(data, {
            ...state,
            error: null,
            isLoading: false,
        }),
    ),
    on(TherapyLimitationsActions.loadTherapyLimitationsFailure, (state, { error }) => ({
        ...state,
        error,
        isLoading: false,
    })),
    on(TherapyLimitationsActions.loadTherapyLimitationsCancelled, state => ({
        ...state,
        isLoading: false,
        error: null,
    })),
    on(TherapyLimitationsActions.updateTherapyLimitations, (state, action) =>
        therapyLimitationsAdapter.updateMany(action.update, state),
    ),
    on(TherapyLimitationsActions.upsertTherapyLimitations, (state, action) =>
        therapyLimitationsAdapter.upsertMany(action.data, state),
    ),
    /**
     * Defensive reset for the MD-9750 stuck-spinner family.
     */
    on(TherapyLimitationsActions.clearTherapyLimitations, EncountersActions.clearEncounter, state =>
        therapyLimitationsAdapter.removeAll({
            ...state,
            isLoading: false,
            error: null,
        }),
    ),
    on(TherapyLimitationsActions.addTherapyLimitationsChanges, (state, { toRemoveIds, toUpdateEntities }) => {
        const newState = therapyLimitationsAdapter.removeMany(toRemoveIds, state);

        return therapyLimitationsAdapter.upsertMany(toUpdateEntities, newState);
    }),
);
